import {
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import DeleteIcon from "@material-ui/icons/Delete";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import axiosInstance from "api/axiosConfig";
import { uploadAPI } from "api/fileManagerApi";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

const useStyles = makeStyles({
  thumb: {
    width: 56,
    height: 40,
    borderRadius: 4,
    objectFit: "cover",
    backgroundColor: "#f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#bdbdbd",
  },
  field: {
    position: "relative",
    height: 160,
    border: "1px dashed #bdbdbd",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    backgroundColor: "#fafafa",
  },
  preview: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
  actions: {
    position: "absolute",
    top: 4,
    right: 4,
    display: "flex",
    backgroundColor: "rgba(255,255,255,0.85)",
    borderRadius: 4,
  },
});

// The widget DTO may carry the file id or a populated file object.
export const getImageId = (image) => image?.id || image || "";

// Public download without the global page loader, so a table of thumbnails
// doesn't flash the app loader once per row.
const useFileImageUrl = (fileId) => {
  const [url, setUrl] = useState("");

  useEffect(() => {
    setUrl("");
    if (!fileId) return undefined;
    let objectUrl = "";
    let active = true;
    axiosInstance
      .get(`filemanager/download/public/${fileId}`, {
        responseType: "blob",
        headers: { design: "Enroute Backoffice" },
      })
      .then((res) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId]);

  return url;
};

export const WidgetImage = ({ image, alt = "" }) => {
  const classes = useStyles();
  const url = useFileImageUrl(getImageId(image));

  if (!url)
    return (
      <div className={classes.thumb}>
        <ImageOutlinedIcon fontSize="small" />
      </div>
    );
  return <img className={classes.thumb} src={url} alt={alt} />;
};

export const WidgetImageField = ({ value, onChange }) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState({ id: "", url: "" });

  // A freshly uploaded file is previewed locally instead of re-downloaded.
  const remoteUrl = useFileImageUrl(
    value && value !== localPreview.id ? value : ""
  );
  const previewUrl = value === localPreview.id ? localPreview.url : remoteUrl;

  useEffect(
    () => () => localPreview.url && URL.revokeObjectURL(localPreview.url),
    [localPreview.url]
  );

  const handleSelect = async ({ target }) => {
    const file = target.files?.[0];
    target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/"))
      return toast.error("Please select an image file");

    setUploading(true);
    try {
      // Public, since the admin panel shows widget images too.
      const res = await uploadAPI(file, undefined, true);
      const id = res?.data?.message?.id;
      if (!id) throw new Error("missing file id");
      setLocalPreview({ id, url: URL.createObjectURL(file) });
      onChange(id);
    } catch (error) {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openPicker = () => inputRef.current && inputRef.current.click();

  return (
    <div className={classes.field}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleSelect}
      />
      {uploading ? (
        <CircularProgress size={32} />
      ) : value ? (
        <>
          {previewUrl ? (
            <img className={classes.preview} src={previewUrl} alt="widget" />
          ) : (
            <CircularProgress size={24} />
          )}
          <div className={classes.actions}>
            <Tooltip title="Change image">
              <IconButton size="small" color="primary" onClick={openPicker}>
                <CloudUploadOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove image">
              <IconButton size="small" onClick={() => onChange("")}>
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            </Tooltip>
          </div>
        </>
      ) : (
        <>
          <Button
            color="primary"
            variant="outlined"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={openPicker}
          >
            Upload Image
          </Button>
          <Typography variant="caption" color="textSecondary" className="mt-2">
            Widget preview image (optional)
          </Typography>
        </>
      )}
    </div>
  );
};
